import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  HelpCircle,
  ArrowRight,
} from "lucide-react";

const faqs = [
  {
    question: "What services does OjMee Technology provide?",
    answer:
      "We provide custom software development, ERP, HRMS, CRM, billing systems, mobile applications, corporate websites, cloud solutions, API integrations, AI automation and business dashboards.",
  },
  {
    question: "Can you build custom software for our business?",
    answer:
      "Yes. We develop custom software based on your business requirements, workflows and goals. Our solutions are designed to be secure, scalable and easy to use.",
  },
  {
    question: "Do you develop Android and iOS mobile applications?",
    answer:
      "Yes. We develop modern mobile applications for Android and iOS using technologies such as React Native and Flutter, depending on your project requirements.",
  },
  {
    question: "Can you integrate AI into existing software?",
    answer:
      "Yes. We can integrate AI assistants, intelligent automation, reporting, data analysis and AI-powered workflows into existing business applications.",
  },
  {
    question: "What technologies do you use?",
    answer:
      "Our technology stack includes React, Next.js, Tailwind CSS, Node.js, Express.js, React Native, Flutter, MongoDB, MySQL, PHP, Python, AWS, Cloudflare and Docker.",
  },
  {
    question: "Do you provide cloud deployment and maintenance?",
    answer:
      "Yes. We provide assistance with cloud deployment, server configuration, databases, APIs, security, backups, monitoring and ongoing maintenance.",
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex((previousIndex) =>
      previousIndex === index ? null : index
    );
  };

  return (
    <section className="relative mx-auto w-full overflow-hidden px-4 py-20 sm:px-6 lg:w-[80%]">

      <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-72 w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-600/10 blur-[130px]" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

        <div className="group relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-pink-500/15 via-violet-700/15 to-white/[0.04] p-7 shadow-[0_30px_100px_rgba(0,0,0,0.3)] backdrop-blur-xl sm:p-10">

          <div
            className="pointer-events-none absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.8) 1px, transparent 1px)",
              backgroundSize: "35px 35px",
            }}
          />

          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-pink-500/15 blur-[90px] transition-all duration-700 group-hover:bg-pink-500/25" />

          <div className="absolute -bottom-32 -left-20 h-72 w-72 rounded-full bg-violet-600/15 blur-[100px]" />

          <div className="relative z-10 flex min-h-[430px] flex-col justify-between">

            <div>

              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-4 py-2">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-pink-400 shadow-[0_0_10px_#ec4899]" />

                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-200">
                  Let's Build Something Great
                </span>
              </div>

              <h2 className="text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl xl:text-5xl">
                Ready to Build Your{" "}
                <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
                  Next Big Idea?
                </span>
              </h2>

              <p className="mt-5 max-w-xl text-sm leading-7 text-slate-400 sm:text-base">
                Turn your ideas into powerful digital products with AI,
                modern technology and expert software engineering.
                Let's build something scalable, secure and future-ready.
              </p>

              <div className="mt-7 space-y-3">

                <div className="flex items-center gap-3 text-xs font-semibold text-slate-400">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.6)]" />
                  AI-Powered Solutions
                </div>

                <div className="flex items-center gap-3 text-xs font-semibold text-slate-400">
                  <span className="h-2 w-2 rounded-full bg-pink-400 shadow-[0_0_10px_rgba(236,72,153,0.6)]" />
                  Enterprise Ready
                </div>

                <div className="flex items-center gap-3 text-xs font-semibold text-slate-400">
                  <span className="h-2 w-2 rounded-full bg-violet-400 shadow-[0_0_10px_rgba(167,139,250,0.6)]" />
                  Scalable Technology
                </div>

              </div>
            </div>

            <div className="mt-10">

              <Link
                to="/contact"
                className="group/btn relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-gradient-to-r from-pink-500 to-violet-700 px-7 py-4 text-sm font-black text-white shadow-[0_15px_40px_rgba(236,72,153,0.25)] transition-all duration-300 hover:scale-105 hover:shadow-[0_20px_55px_rgba(236,72,153,0.4)]"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover/btn:translate-x-full" />

                <span className="relative">
                  Start Your Project
                </span>

                <ArrowRight className="relative h-5 w-5 transition-transform duration-300 group-hover/btn:translate-x-1" />
              </Link>

              <p className="mt-3 text-[10px] uppercase tracking-widest text-slate-600">
                Let's talk about your idea
              </p>

            </div>

          </div>
        </div>

        <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/[0.05] p-7 shadow-[0_30px_100px_rgba(0,0,0,0.3)] backdrop-blur-xl sm:p-10">

          <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-violet-600/10 blur-[90px]" />

          <div className="relative z-10">

            <div className="mb-7">

              <div className="inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/5 px-4 py-2 text-violet-300">

                <HelpCircle className="h-4 w-4" />

                <span className="text-[10px] font-bold uppercase tracking-[0.25em]">
                  FAQ
                </span>

              </div>

              <h2 className="mt-5 text-3xl font-black text-white sm:text-4xl">
                Frequently Asked

                <span className="block bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                  Questions
                </span>
              </h2>

              <p className="mt-3 text-sm leading-6 text-slate-400">
                Quick answers about our services, technology and
                development process.
              </p>

            </div>

            <div className="space-y-3">

              {faqs.slice(0, 5).map((faq, index) => {
                const isOpen = openIndex === index;

                return (
                  <div
                    key={faq.question}
                    className={`overflow-hidden rounded-2xl border transition-all duration-300 ${
                      isOpen
                        ? "border-pink-400/30 bg-white/[0.07]"
                        : "border-white/10 bg-white/[0.03] hover:border-white/20"
                    }`}
                  >

                    <button
                      type="button"
                      onClick={() => toggleFAQ(index)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center justify-between gap-4 p-4 text-left"
                    >

                      <div className="flex items-center gap-3">

                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[10px] font-black ${
                            isOpen
                              ? "bg-gradient-to-br from-pink-500 to-violet-700 text-white"
                              : "bg-white/[0.06] text-slate-500"
                          }`}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span className="text-sm font-bold text-slate-200">
                          {faq.question}
                        </span>

                      </div>

                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-300 ${
                          isOpen ? "rotate-180 text-pink-400" : ""
                        }`}
                      />

                    </button>

                    <AnimatePresence initial={false}>

                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >

                          <p className="px-4 pb-5 pl-[4.25rem] text-xs leading-6 text-slate-400">
                            {faq.answer}
                          </p>

                        </motion.div>
                      )}

                    </AnimatePresence>

                  </div>
                );
              })}

            </div>

          </div>
        </div>

      </div>
    </section>
  );
}